/**
 * AI bot roster parsing, validation, and display ordering.
 *
 * The client is intentionally decoupled from individual champion models: it
 * receives a roster of { id, label, url } entries via runtime config (the
 * GAME_AI_BOTS env var) and renders whatever it is given. Adding or removing a
 * champion is a config-only change — no client code edit, no new build wiring.
 *
 * Everything here is pure and total: a malformed roster degrades to "no bots"
 * (AI invite hidden) rather than throwing or inviting a bad URL.
 */

export interface AiBotEntry {
  /** Stable identifier used as the <select> value. */
  readonly id: string;
  /** Human-readable model name shown to the user (e.g. "ai-fp-gen067"). */
  readonly label: string;
  /** Bot worker base URL the client POSTs /invite to. */
  readonly url: string;
}

export interface NumberedAiBot extends AiBotEntry {
  /** 1-based version sequence — older champion = lower number. */
  readonly seq: number;
  /** Label with sequence prefix for display, e.g. "5. ai-fp-gen067". */
  readonly displayLabel: string;
}

const HTTP_URL = /^https?:\/\/.+/;

function isValidEntry(value: unknown): value is AiBotEntry {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" &&
    entry.id.length > 0 &&
    typeof entry.label === "string" &&
    entry.label.length > 0 &&
    typeof entry.url === "string" &&
    HTTP_URL.test(entry.url)
  );
}

function sanitizeRoster(value: unknown): readonly AiBotEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<string>();
  const roster: AiBotEntry[] = [];
  for (const item of value) {
    if (!isValidEntry(item) || seen.has(item.id)) {
      continue;
    }
    seen.add(item.id);
    roster.push({ id: item.id, label: item.label, url: item.url });
  }
  return Object.freeze(roster);
}

/**
 * Parse and validate the GAME_AI_BOTS JSON into a gameType → roster map.
 *
 * Misuse guard: malformed JSON, wrong top-level shape, non-array rosters, and
 * individual bad entries (missing/empty fields, non-http URLs, duplicate ids)
 * are all dropped silently. A config typo can never break the client or cause
 * an invite to an unvalidated URL.
 */
export function parseAiBots(
  json: string | undefined,
): Readonly<Record<string, readonly AiBotEntry[]>> {
  if (!json || json.trim() === "") {
    return Object.freeze({});
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return Object.freeze({});
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return Object.freeze({});
  }

  const result: Record<string, readonly AiBotEntry[]> = {};
  for (const [gameType, roster] of Object.entries(parsed)) {
    const sanitized = sanitizeRoster(roster);
    if (sanitized.length > 0) {
      result[gameType] = sanitized;
    }
  }
  return Object.freeze(result);
}

/**
 * Number a chronological (oldest-first) roster and return it latest-first for
 * display: the newest champion gets the highest sequence number and sits at the
 * top of the list (e.g. "5. ai-fp-gen067" … down to … "1. ai-fp-gen024").
 */
export function orderBotsForDisplay(
  roster: readonly AiBotEntry[],
): readonly NumberedAiBot[] {
  return roster
    .map((entry, index) => {
      const seq = index + 1;
      return { ...entry, seq, displayLabel: `${seq}. ${entry.label}` };
    })
    .reverse();
}
